import type { Lot } from '../types';
import type { HouseStreets } from '../houseStreets';

export type BlockLot = Pick<Lot, 'sourceId' | 'side' | 'ordinal' | 'kind'>;

/** The street side a lot stands on, and its block side there: the stretch between two cross ways. */
export interface BlockKeys {
  readonly side: string;
  readonly block: string;
}

function sideKey(lot: BlockLot): string {
  return `${lot.sourceId}:${lot.side}`;
}

/* Where a cross way meets a street side: at the junction of each way a corner house of that side has beside it. */
function cutsBySide(
  lots: readonly BlockLot[],
  streets: readonly HouseStreets[]
): Map<string, number[]> {
  const cuts = new Map<string, number[]>();
  lots.forEach((lot, i) => {
    const s = streets[i]!;
    if (lot.kind !== 'house') return;
    for (const way of [s.left, s.right]) {
      const at = way && s.junctions.find((j) => j.id === way.id)?.along;
      if (at === undefined || at === null) continue;
      const known = cuts.get(sideKey(lot)) ?? [];
      if (!known.includes(at)) cuts.set(sideKey(lot), [...known, at]);
    }
  });
  return cuts;
}

/**
 * Each lot's street side and block side. A house's block counts the cross ways before it along its
 * street; a house on a square or the shore has no place along a way and stands in the side's one
 * block; a back house stands in the block of the house in front of it.
 */
export function blockKeysOf(
  lots: readonly BlockLot[],
  streets: readonly HouseStreets[]
): BlockKeys[] {
  const cuts = cutsBySide(lots, streets);
  const blockOfHouse = (lot: BlockLot, along: number | null): number =>
    along === null ? 0 : (cuts.get(sideKey(lot)) ?? []).filter((c) => c < along).length;
  const fronts = new Map<string, number>();
  lots.forEach((lot, i) => {
    if (lot.kind === 'house')
      fronts.set(`${sideKey(lot)}:${lot.ordinal}`, blockOfHouse(lot, streets[i]!.along));
  });
  return lots.map((lot, i) => {
    const n =
      lot.kind === 'house'
        ? blockOfHouse(lot, streets[i]!.along)
        : (fronts.get(`${sideKey(lot)}:${lot.ordinal}`) ?? 0);
    return { side: sideKey(lot), block: `${sideKey(lot)}:${n}` };
  });
}
